import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 3,
  id: "content-lab-federated-import-video-button",
  class: "content-lab-root"
}

import { retrieve_4 as retrieveVideoLibraryAsset } from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

import UploadVideo from '../UploadVideo.vue';
import { transformRawVideoAsset, VideoAsset } from '../videoLibraryService';

import { openNewContentModal } from '@/studio/contentLab/newContentModal';
import renderReactComponent from '@/studio/contentLab/renderReactComponent';
import TablerPlus from '@/studioComponents/Icons/tabler/TablerPlus.vue';
import StudioButton, { StudioButtonVariant } from '@/studioComponents/StudioButton.vue';

const MAX_RETRIES = 3;

export default /*#__PURE__*/_defineComponent({
  __name: 'VideoLibraryUploadButton',
  props: {
    buttonVariant: {},
    uploadFlow: {}
  },
  emits: ["upload:success"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const isUploadingNewFile = ref(false);
const microfrontendFailed = ref(false);
let retryCount = 0;
let unmount: (() => void) | null = null;
let retryTimeout: NodeJS.Timeout | null = null;

const store = useStore();

// The simplified "New upload" modal only rides the content-lab federated pipeline, so it must
// only appear where that pipeline already runs (content-lab-import and content-lab-federated),
// and only once the org has the FF on. Everyone else keeps the original per-uploadFlow UI below.
const isSimplifiedUploadEnabled = computed(
  () =>
    Boolean(store?.getters['org/getCurrentOrg']?.Use_CL_Simplified_Upload) &&
    (props.uploadFlow === 'content-lab-federated' || props.uploadFlow === 'content-lab-import')
);

const loadMicrofrontend = () => {
  if (!document.getElementById('content-lab-federated-import-video-button')) {
    return;
  }

  import('content_studio/renderVideoImportDialog' as any)
    .then(({ default: renderVideoImportDialog }) => {
      unmount = renderReactComponent(
        renderVideoImportDialog,
        'content-lab-federated-import-video-button',
        {},
        {
          onSuccess: async (videoAssetId: string) => {
            store.dispatch('studioToast/triggerToast', {
              msg: 'Video imported successfully',
              mode: 'success'
            });

            if (!videoAssetId) {
              emit('upload:success', undefined);
              return;
            }

            try {
              const videoAsset = await retrieveVideoLibraryAsset({ id: videoAssetId });
              const transformedVideoAsset = transformRawVideoAsset(videoAsset);
              emit('upload:success', transformedVideoAsset);
            } catch (error) {
              emit('upload:success', undefined);
            }
          }
        }
      );
      // Reset retry count and failure flag if successful
      retryCount = 0;
      microfrontendFailed.value = false;
    })
    .catch(err => {
      logger.error(
        `[VideoLibraryUploadButton] Failed importing content_studio/renderVideoImportDialog (attempt ${retryCount + 1}/${MAX_RETRIES})`,
        err
      );
      retryCount++;

      if (retryCount < MAX_RETRIES) {
        // Retry with exponential backoff (1s, 2s, 4s, etc.)
        const delay = Math.pow(2, retryCount - 1) * 1000;
        logger.info(`[VideoLibraryUploadButton] Retrying in ${delay}ms...`);
        retryTimeout = setTimeout(loadMicrofrontend, delay);
      } else {
        logger.error(`[VideoLibraryUploadButton] Failed to load microfrontend after ${MAX_RETRIES} attempts`);
        microfrontendFailed.value = true;
        store.dispatch('studioToast/triggerToast', {
          msg: 'Something went wrong while initializing video import. Please refresh the page and try again.',
          mode: 'error'
        });
      }
    });
};

onMounted(() => {
  if (!isSimplifiedUploadEnabled.value && props.uploadFlow === 'content-lab-federated') {
    loadMicrofrontend();
  }
});

onUnmounted(() => {
  if (unmount) {
    unmount();
  }

  if (retryTimeout) {
    clearTimeout(retryTimeout);
  }
});

const onUploadSuccess = (newVideoAsset: VideoAsset) => {
  emit('upload:success', newVideoAsset);
};

const onUploadInit = () => {
  isUploadingNewFile.value = true;
};

const onUploadFailure = () => {
  isUploadingNewFile.value = false;
};

const onUploadClose = () => {
  isUploadingNewFile.value = false;
};

const navigateToContentLabImport = () => {
  store.dispatch('contentStudio/openContentLabImport');
};

__expose({
  isUploadingNewFile
});

return (_ctx: any,_cache: any) => {
  return (isSimplifiedUploadEnabled.value)
    ? (_openBlock(), _createBlock(StudioButton, {
        key: 0,
        trackingId: "new-content-upload-button",
        variant: _ctx.buttonVariant,
        onClick: _unref(openNewContentModal)
      }, {
        icon: _withCtx(() => [
          _createVNode(TablerPlus)
        ]),
        default: _withCtx(() => [
          _createTextVNode(" Upload ")
        ]),
        _: 1
      }, 8, ["variant", "onClick"]))
    : (_ctx.uploadFlow === 'legacy')
      ? (_openBlock(), _createBlock(UploadVideo, {
          key: 1,
          onSuccess: onUploadSuccess,
          onInit: onUploadInit,
          onFailure: onUploadFailure,
          onClose: onUploadClose
        }, {
          default: _withCtx(() => [
            _createVNode(StudioButton, {
              trackingId: "upload-video-button",
              variant: _ctx.buttonVariant
            }, {
              icon: _withCtx(() => [
                _createVNode(TablerPlus)
              ]),
              default: _withCtx(() => [
                _createTextVNode(" Upload Video ")
              ]),
              _: 1
            }, 8, ["variant"])
          ]),
          _: 1
        }))
      : (_ctx.uploadFlow === 'content-lab-import')
        ? (_openBlock(), _createBlock(StudioButton, {
            key: 2,
            trackingId: "content-lab-import-video-button",
            variant: _ctx.buttonVariant,
            onClick: navigateToContentLabImport
          }, {
            icon: _withCtx(() => [
              _createVNode(TablerPlus)
            ]),
            default: _withCtx(() => [
              _createTextVNode(" Import Video ")
            ]),
            _: 1
          }, 8, ["variant"]))
        : (_ctx.uploadFlow === 'content-lab-federated' && !microfrontendFailed.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1))
          : _createCommentVNode("", true)
}
}

})